import { UserPreference } from "./config";
import getInstallationMode from "./utils/getInstallationMode";
import getOS from "./utils/getOS";
import { Session } from "./types/TeacherTrainingSession";

export interface Asset {
    href: string;
    title: string;
    imgSrc?: string;
}
interface TOCItem {
    title: string;
    href: string;
}

type Hotspot = { id: string; page_no: string };

type OpfResult = {
    opfDocument: Document | null;
    baseFolder: string | null;
};

const fetchTOC = async (uri: string): Promise<TOCItem[]> => {
    const response = await fetch(uri);
    if (!response.ok) {
        throw new Error(`Failed to fetch ${uri}: ${response.statusText}`);
    }
    const pageHtml = await response.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(pageHtml, 'application/xml');

    const items = Array.from(doc.querySelectorAll('nav#toc li a'));

    return items.map((item) => ({
        title: item.textContent || '',
        href: item.getAttribute('href') || '',
    }));
};

export const extractAssets = (doc: Document): Asset[] => {
    const assets: Asset[] = [];
    const assetElements = doc.querySelectorAll<
        HTMLAnchorElement | HTMLImageElement
    >('a[href]');
    assetElements.forEach((element) => {
        let href: string | undefined;
        let title: string | undefined;
        let imgSrc: string | undefined;

        if (element instanceof HTMLAnchorElement) {
            href = element.getAttribute('href') ?? '';
            title = element.getAttribute('title') ?? '';
        } else if (element instanceof HTMLImageElement) {
            imgSrc = element.getAttribute('src') ?? '';
        }

        if (href || imgSrc) {
            assets.push({
                href: href ?? '',
                title: title ?? '',
                imgSrc: imgSrc ?? '',
            });
        }
    });

    return assets;
};

export const fetchPageHtml = async (pageUrl: string): Promise<Asset[]> => {
    try {
        const response = await fetch(pageUrl);
        if (!response.ok) {
            throw new Error(`Failed to fetch ${pageUrl}`);
        }
        const pageHtml = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(pageHtml, 'text/html');
        return extractAssets(doc);
    } catch (error) {
        console.error('Error fetching page HTML:', error);
        return [];
    }
};

const fetchOpfFile = async (opfUri: string): Promise<Document | null> => {
    try {
        const response = await fetch(opfUri);
        if (!response.ok) {
            throw new Error(`Failed to fetch ${opfUri}`);
        }
        const opfContent = await response.text();
        const parser = new DOMParser();
        return parser.parseFromString(opfContent, 'application/xhtml+xml');
    } catch (error: any) {
        console.error(`Error fetching OPF file: ${error.message}`);
        return null;
    }
};

export const getContentOpf = async (bookUrl: string): Promise<OpfResult> => {
    const possibleBaseFolders = ['OPS', 'OEBPS'];
    const opfFiles = ['content.opf', 'package.opf'];

    for (const folder of possibleBaseFolders) {
        for (const file of opfFiles) {
            const opfUri = `${bookUrl}/${folder}/${file}`;
            const opfDocument = await fetchOpfFile(opfUri);
            if (opfDocument) {
                return { opfDocument, baseFolder: folder };
            }
        }
    }

    console.error('OPF file could not be found in any of the specified paths.');
    return { opfDocument: null, baseFolder: null };
};

export const extractHotspots = async (
    bookUrl: string
): Promise<Set<Hotspot>> => {
    const opfData = await getContentOpf(bookUrl);
    const opfDocument = opfData?.opfDocument;
    const hotspotSet = new Set<Hotspot>();
    if (opfDocument) {
        const items = opfDocument.querySelectorAll(
            'item[media-type="application/xhtml+xml"]'
        );
        items.forEach((element: Element) => {
            const xhtmlId = element.getAttribute('id');
            const pageNo = element.getAttribute('pageNum');
            if (xhtmlId?.startsWith('pg')) {
                hotspotSet.add({ id: xhtmlId, page_no: pageNo || '' });
            }
        });
    }
    return hotspotSet;
};

export const getTOC = async (bookUrl: string): Promise<TOCItem[]> => {
    const opfData = await getContentOpf(bookUrl);
    const opfDocument = opfData?.opfDocument;
    const docPath = opfData?.baseFolder;
    const navItem = opfDocument?.querySelector(
        'item[properties="nav"][media-type="application/xhtml+xml"]'
    );
    const toc = navItem?.getAttribute('href');

    const tocUri = `${bookUrl}/${docPath}/${toc}`;
    try {
        return await fetchTOC(tocUri);
    } catch (error) {
        console.error('toc or nav xhtml file not present');
        return [];
    }
};

export const getProductSearchXml = async (
    bookPath: string
): Promise<string | null> => {
    const opsXmlPath = `${bookPath}/OPS/productsearch.xml`;
    const oebpsXmlPath = `${bookPath}/OEBPS/productsearch.xml`;

    try {
        // Attempt to fetch from OPS directory
        let xmlResponse = await fetch(opsXmlPath);
        if (xmlResponse.ok) {
            return xmlResponse.text();
        }

        console.log(
            'OPS/productsearch.xml not found. Attempting OEBPS directory...'
        );

        // Attempt to fetch from OEBPS directory
        xmlResponse = await fetch(oebpsXmlPath);
        if (xmlResponse.ok) {
            return await xmlResponse.text();
        }

        console.error(
            'Failed to fetch productsearch.xml from both OPS and OEBPS directories.'
        );
        return null;
    } catch (fetchError) {
        console.error(
            'An error occurred while fetching productsearch.xml:',
            fetchError
        );
        return null;
    }
};

export const getInitialPage = (): number => {
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const pageParam = params.get('page');
        const parsed = parseInt(pageParam || '', 10);
        if (!isNaN(parsed) && parsed >= 0) {
            return parsed;
        }
    }
    return 0; // Default page
};

export const getInitialActiveTab = (): 'openBook' | 'assets' => {
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('activeTab');
        if (tab === 'assets' || tab === 'openBook') {
            return tab;
        }
    }
    return 'openBook';
};
const modifyBaseUrlFor = (
    baseUrl: string,
    type: 'windows' | 'android' | 'webgl'
) => {
    baseUrl = baseUrl.replace(
        /\/(windows|webgl|android)\//,
        `/${type}/`
    );
    if (baseUrl.indexOf(`/${type}/`) === -1) {
        baseUrl = `${baseUrl}/${type}/assets/index.html`;
    }
    return baseUrl;
};

const toBase64 = (input: string) => {
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(input).toString('base64'); // Node.js
    } else {
        return btoa(unescape(encodeURIComponent(input))); // Browser
    }
};

export const getARIResourceUrl = async (
    asset_url: string,
    history: {
        additional_metadata: {
            jump_off_point: string | null;
            manifest: object | null;
            scene_name: string | null;
        } | null;
        updated_at: string;
    }[],
    cookie: object
): Promise<string> => {
    const os = getOS();
    let installationMode =
        getInstallationMode() === 'unknown'
            ? process.env.REACT_APP_INSTALLATION_MODE
            : getInstallationMode();
    if (!history || !history.length) {
        history = [
            {
                additional_metadata: {
                    manifest: {},
                    jump_off_point: null,
                    scene_name: null,
                },
                updated_at: new Date().valueOf().toString(),
            },
        ];
    }

    // Get the latest history entry by sorting based on `updated_at`
    const lastHistory = [...history] // Clone array to avoid modifying the original
        .sort(
            (a, b) =>
                new Date(b.updated_at).getTime() -
                new Date(a.updated_at).getTime()
        )[0];

    // Extract metadata
    const { manifest, jump_off_point, scene_name } =
        lastHistory.additional_metadata || {
            manifest: {},
            jump_off_point: null,
            scene_name: null
        };

    // Convert manifest and url_link to Base64
    const manifestBase64 = toBase64(JSON.stringify(manifest));
    const urlLinkBase64 = toBase64(
        process.env.REACT_APP_ANALYTICS_API_HOST || ''
    );
    const cookieBase64 = toBase64(JSON.stringify(cookie))
    let base_url = asset_url;
    let openARIModelUsing =
        UserPreference.getInstance().getValue<string>('openARIModelUsing');

    if (installationMode === 'server') {
        // run webgl
        base_url = modifyBaseUrlFor(base_url, 'webgl');
    } else if (installationMode === 'desktop') {
        // desktop
        if (os === 'windows') {
            base_url = modifyBaseUrlFor(
                base_url,
                openARIModelUsing === 'Web Player' ? 'webgl' : 'windows'
            );
        // } else if (os === 'android') {
        //     base_url = modifyBaseUrlFor(
        //         base_url,
        //         openARIModelUsing === 'Web Player' ? 'webgl' : 'android'
        //     );
        } else {
            base_url = modifyBaseUrlFor(base_url, 'webgl');
        }
    } else {
        // cloud website
        // if (os === 'android') {
        //     base_url = modifyBaseUrlFor(
        //         base_url,
        //         'android'
        //     );
        // } else {
            base_url = modifyBaseUrlFor(base_url, 'webgl');
    }

    base_url += `?manifest=${encodeURIComponent(manifestBase64)}&jumpoff=${encodeURIComponent(jump_off_point || '')}&url=${encodeURIComponent(urlLinkBase64)}&sceneName=${scene_name}&cookie=${encodeURIComponent(cookieBase64)}`;
    // Construct final URL
    return base_url;
};


//Teacher Training helper functions
export const createDateTime = (date:string, time:string) => {
  // Parse date like "Aug 05, 2025"
  const dateObj = new Date(date);
  
  // Parse time like "4:00 PM"
  const [timeStr, period] = time.split(' ');
  const [hours, minutes] = timeStr.split(':');
  let hour24 = parseInt(hours);
  
  if (period === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (period === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  
  dateObj.setHours(hour24, parseInt(minutes), 0, 0);
  return dateObj;
};

// Get current active session based on time logic
export const getCurrentSession = (
    currentTime: Date,
    sessions: Session[]
): { currentSession: Session | null; withinOneWeek: boolean } => {
    const now = currentTime;

    // First: Check for current ongoing session
    for (const session of sessions) {
        const startTime = createDateTime(session.date, session.start_time);
        const endTime = createDateTime(session.date, session.end_time);

        if (now >= startTime && now <= endTime) {
            return { currentSession: session, withinOneWeek: true };
        }
    }

    // Filter upcoming sessions
    const upcomingSessions = sessions.filter((session: Session) => {
        const startTime = createDateTime(session.date, session.start_time);
        return startTime > now;
    });

    // Sort by start time and pick the earliest
    upcomingSessions.sort((a: Session, b: Session) => {
        const timeA:any = createDateTime(a.date, a.start_time);
        const timeB:any = createDateTime(b.date, b.start_time);
        return timeA - timeB;
    });

    const nextSession = upcomingSessions[0] || null;

    if (!nextSession) {
        return { currentSession: null, withinOneWeek: false };
    }

    // Check if it's within 7 days
    const nextStart = createDateTime(nextSession.date, nextSession.start_time);
    const diffInDays = (nextStart.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

    return {
        currentSession: nextSession,
        withinOneWeek: diffInDays <= 7
    };
};
  export const formatDisplayDate = (dateString:string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };


     // Get status text
  export const getStatusText = (sessionState:string) => {
    switch (sessionState) {
      case 'ongoing':
        return 'Live now';
      case 'starting_soon':
        return 'Starting soon';
      default:
        return 'Upcoming live training for teachers on Lead Group Academy!';
    }
  };


  // Determine session state and button type
  export const getSessionState = (currentSession:Session,currentTime:Date) => {
    if (!currentSession) return 'no_session';
    
    const now = currentTime;
    const startTime = createDateTime(currentSession.date, currentSession.start_time);
    const endTime = createDateTime(currentSession.date, currentSession.end_time);
    const thirtyMinutesBefore = new Date(startTime.getTime() - 30 * 60 * 1000);
    
    if (now >= startTime && now <= endTime) {
      return 'ongoing'; // Show join session
    } else if (now >= thirtyMinutesBefore && now < startTime) {
      return 'starting_soon'; // Show join session
    } else {
      return 'upcoming'; // Show view calendar
    }
  };